/**
 * Geography-oriented message path utils
 */

import { CanonicalDataType } from "./MessagePathRecord";

export type LatitudeDataType =
  | CanonicalDataType.LatDegFloat
  | CanonicalDataType.LatDegInt
  | CanonicalDataType.Number;

export type LongitudeDataType =
  | CanonicalDataType.LonDegFloat
  | CanonicalDataType.LonDegInt
  | CanonicalDataType.Number;

export function isLatitudeType(
  dataType: CanonicalDataType,
): dataType is LatitudeDataType {
  return (
    dataType === CanonicalDataType.LatDegFloat ||
    dataType === CanonicalDataType.LatDegInt ||
    dataType === CanonicalDataType.Number
  );
}

export function isLongitudeType(
  dataType: CanonicalDataType,
): dataType is LongitudeDataType {
  return (
    dataType === CanonicalDataType.LonDegFloat ||
    dataType === CanonicalDataType.LonDegInt ||
    dataType === CanonicalDataType.Number
  );
}

export function isGeoSpecificType(dataType: CanonicalDataType): boolean {
  return (
    dataType === CanonicalDataType.LatDegFloat ||
    dataType === CanonicalDataType.LatDegInt ||
    dataType === CanonicalDataType.LonDegFloat ||
    dataType === CanonicalDataType.LonDegInt
  );
}

/** Which half of a coordinate pair a field supplies. */
export type CoordinateRole = "latitude" | "longitude";

/**
 * The role a geo-specific data type carries, or undefined for a type that
 * carries none. `isLatitudeType` and `isLongitudeType` are both true of a
 * plain number, so neither can answer this.
 */
export function geoSpecificRole(
  dataType: CanonicalDataType,
): CoordinateRole | undefined {
  switch (dataType) {
    case CanonicalDataType.LatDegFloat:
    case CanonicalDataType.LatDegInt:
      return "latitude";
    case CanonicalDataType.LonDegFloat:
    case CanonicalDataType.LonDegInt:
      return "longitude";
    default:
      return undefined;
  }
}

/**
 * Field-name tokens that name a coordinate's half. Read only when the data
 * type doesn't say — see {@link namesFormCoordinatePair}.
 */
const LATITUDE_NAME_TOKENS = new Set(["lat", "latitude"]);
const LONGITUDE_NAME_TOKENS = new Set(["lon", "lng", "long", "longitude"]);

/**
 * Split a field name into lowercase tokens on punctuation and camelCase
 * humps, so `gps_lat`, `gpsLat` and `lat` all yield a bare `lat` token to
 * compare. Digits stay attached to their word: `pos1_lat` is
 * ["pos1", "lat"].
 */
function coordinateNameTokens(name: string): string[] {
  return name
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[^A-Za-z0-9]+/)
    .filter((token) => token.length > 0)
    .map((token) => token.toLowerCase());
}

/**
 * The role `nameA` plays when the two field names spell a latitude/longitude
 * pair, or undefined when they don't.
 *
 * Names pair when their token sequences are the same length and differ at
 * exactly one position, where one side holds a latitude token and the other
 * a longitude token: `gps_lat`/`gps_lon`, `lat`/`lng`,
 * `min_latitude`/`min_longitude`, `compensated_position.lat`/`.lon`.
 *
 * Requiring every other token to match is the whole safety property. It is
 * what keeps `lat_std_dev_m` away from `lon_accel`, and it leaves a name with
 * no counterpart — ArduPilot's `NLon` ("number of long loops"), PX4's
 * `lat_lon_reset_counter` — paired with nothing rather than with the nearest
 * plausible sibling. A field whose partner cannot be named this way is
 * refused, not guessed at.
 */
export function namesFormCoordinatePair(
  nameA: string,
  nameB: string,
): CoordinateRole | undefined {
  const tokensA = coordinateNameTokens(nameA);
  const tokensB = coordinateNameTokens(nameB);
  if (tokensA.length === 0 || tokensA.length !== tokensB.length) {
    return undefined;
  }

  let differingIndex = -1;
  for (let i = 0; i < tokensA.length; i += 1) {
    if (tokensA[i] !== tokensB[i]) {
      if (differingIndex !== -1) {
        return undefined;
      }
      differingIndex = i;
    }
  }
  if (differingIndex === -1) {
    return undefined;
  }

  const tokenA = tokensA[differingIndex];
  const tokenB = tokensB[differingIndex];
  if (LATITUDE_NAME_TOKENS.has(tokenA) && LONGITUDE_NAME_TOKENS.has(tokenB)) {
    return "latitude";
  }
  if (LONGITUDE_NAME_TOKENS.has(tokenA) && LATITUDE_NAME_TOKENS.has(tokenB)) {
    return "longitude";
  }
  return undefined;
}

export function getCorrespondingGeoType(
  initialType: CanonicalDataType,
): CanonicalDataType[] {
  switch (initialType) {
    case CanonicalDataType.LatDegFloat:
      return [CanonicalDataType.LonDegFloat];
    case CanonicalDataType.LonDegFloat:
      return [CanonicalDataType.LatDegFloat];
    case CanonicalDataType.LatDegInt:
      return [CanonicalDataType.LonDegInt];
    case CanonicalDataType.LonDegInt:
      return [CanonicalDataType.LatDegInt];
    case CanonicalDataType.Number:
      return [
        CanonicalDataType.LatDegFloat,
        CanonicalDataType.LonDegFloat,
        CanonicalDataType.LatDegInt,
        CanonicalDataType.LonDegInt,
        CanonicalDataType.Number,
      ];
    default:
      throw new Error("Unsupported canonical data type");
  }
}
